import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../services/adminService';
import { useToast } from '../hooks/use-toast';
import { Plus } from 'lucide-react';
import WarehouseCard from './WarehouseCard';
import WarehouseFormModal, { INITIAL_FORM } from './WarehouseFormModal';

const AdminWarehouses = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState(INITIAL_FORM);

  const loadWarehouses = useCallback(async () => {
    try {
      const response = await adminService.getWarehouses();
      setWarehouses(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load warehouses', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const warehouseData = {
      ...formData,
      area_sqm: parseFloat(formData.area_sqm),
      price: formData.price ? parseFloat(formData.price) : null,
      features: formData.features ? formData.features.split(',').map(f => f.trim()) : [],
      images: formData.images ? formData.images.split(',').map(img => img.trim()) : []
    };

    try {
      if (editingWarehouse) {
        await adminService.updateWarehouse(editingWarehouse.id, warehouseData);
        toast({ title: 'Success', description: 'Warehouse updated' });
      } else {
        await adminService.createWarehouse(warehouseData);
        toast({ title: 'Success', description: 'Warehouse created' });
      }
      setShowModal(false);
      setEditingWarehouse(null);
      setFormData(INITIAL_FORM);
      loadWarehouses();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save warehouse', variant: 'destructive' });
    }
  };

  const handleEdit = (warehouse) => {
    setEditingWarehouse(warehouse);
    setFormData({
      unit_number: warehouse.unit_number,
      area_sqm: warehouse.area_sqm.toString(),
      status: warehouse.status,
      price: warehouse.price ? warehouse.price.toString() : '',
      features: warehouse.features.join(', '),
      images: warehouse.images.join(', ')
    });
    setShowModal(true);
  };

  const handleDelete = async (warehouseId) => {
    if (window.confirm('Are you sure you want to delete this warehouse?')) {
      try {
        await adminService.deleteWarehouse(warehouseId);
        toast({ title: 'Success', description: 'Warehouse deleted' });
        loadWarehouses();
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to delete warehouse', variant: 'destructive' });
      }
    }
  };

  const openAddModal = () => {
    setEditingWarehouse(null);
    setFormData(INITIAL_FORM);
    setShowModal(true);
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Warehouse Inventory</h2>
          <p className="text-gray-600 mt-2">Manage industrial warehouse units</p>
        </div>
        <button
          onClick={openAddModal}
          data-testid="add-warehouse-btn"
          className="flex items-center gap-2 bg-amber-600 text-white px-6 py-3 rounded-lg hover:bg-amber-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Warehouse
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-3 text-center py-12 text-gray-500">Loading...</div>
        ) : warehouses.length === 0 ? (
          <div className="col-span-3 text-center py-12 text-gray-500">
            No warehouses found. Add your first warehouse unit!
          </div>
        ) : (
          warehouses.map((warehouse) => (
            <WarehouseCard
              key={warehouse.id}
              warehouse={warehouse}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      {showModal && (
        <WarehouseFormModal
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onClose={() => { setShowModal(false); setEditingWarehouse(null); }}
          isEditing={!!editingWarehouse}
        />
      )}
    </div>
  );
};

export default AdminWarehouses;
